import { useParams } from 'react-router-dom'
import cn from 'clsx'
import { parseISO } from 'date-fns'
import styles from './RequestLoan.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {
  Button,
  Block,
  Text,
  Modal,
  IconButton,
  IconTitle,
  DotsIcon,
  MoneyBanknotesRotatedThirdIcon,
} from 'common/components'
import { formatMoney } from 'common/helpers'
import { RequestPeriod, LoanForm, useGetRequest, isMilestonePassed } from '..'

interface StatInterface {
  value: number
  label: string
  active: boolean
}

interface StatProps {
  stat: StatInterface,
  request: any
  addBounts: boolean
}

interface EditProps {
  request: any
  showAddBtn?: boolean
}

export default function RequestLoan() {
  const params = useParams()
  const { data: request } = useGetRequest(params.uuid)
  const permissions: PermissionType[] = useStore((state) => state.permissions)

  const isConfirmed = Boolean(request.loanValue)
  const isControllerCheck =
    (!!permissions?.includes('edit loan request (fc check)')) && request.status === 'financial-controller-check'
  const isEdit =
    isControllerCheck ||
    ((!!permissions?.includes('edit loan request (lm check)')) && request.status === 'lending-manager-check')

  const stats: StatInterface[] = [
    {
      value: request.interest,
      label: 'Interest',
      active: isConfirmed,
    },
    {
      value: request.establishFee,
      label: 'Estab. fee',
      active: isConfirmed,
    },
    { value: request.lineFee, label: 'Line fee(M)', active: isConfirmed },
    { value: request.lvrMax, label: 'LVR max', active: isConfirmed },
    { value: request.irr, label: 'Loan IRR', active: isConfirmed },
    {
      value: !permissions?.includes('view bonus') ? null : request.bonus,
      label: 'Bonus',
      active: Boolean(request.bonus)
    },
  ]

  return (
    <Block className={styles.root} type="white">
      <div className={styles.block}>
        <IconTitle icon={<MoneyBanknotesRotatedThirdIcon />}>Loan</IconTitle>
        <div>
          <Text size="3xl" mb={6}>
            {formatMoney(request.loanValue || request.loanValueRequested)}
          </Text>
          <Text size="xs" color="grey">
            Requested {formatMoney(request.loanValueRequested)}
          </Text>
        </div>
      </div>
      <div className={styles.block}>
        <RequestPeriod
          estimated={!isConfirmed}
          from={parseISO(request.openDate)}
          to={parseISO(request.maturityDate)}
        />
      </div>
      <div className={styles.block}>
        {isEdit && !isControllerCheck && (
          <Edit request={request} />
        )}
        <ul className={styles.stats}>
          {stats.filter(
            (stat) => ((stat?.value !== null) && (stat?.value !== undefined))
          ).map((stat) => (
            <Stat key={stat.label} stat={stat} request={request} addBounts={isEdit && isControllerCheck} />
          ))}
        </ul>
      </div>
    </Block>
  )
}

const Stat = ({ stat, request, addBounts }: StatProps) => {
  const isBonus = stat.label === 'Bonus';
  return (
    <div>
      <Text size="xs" mb={2} color="grey">
        {stat.label}
      </Text>
      {
        isBonus ? (
          <div className={cn(styles.bonus_text)}>
            <Text
              size="xl"
              weight="demiBold"
              color={stat.value ? undefined : isMilestonePassed("bonus-granted", request) ? undefined : 'black50'}
            >
              {stat.value}%
            </Text>
            {addBounts && <Edit showAddBtn={true} request={request} />}
          </div>
        ) : (
          <Text
            size="xl"
            weight="demiBold"
            color={stat.value ? undefined : 'black50'}
          >
            {stat.value}%
          </Text>
        )
      }

    </div>
  )
}

const Edit = ({ showAddBtn = false, request }: EditProps) => {
  const isLoanOpen = useStore((state) => state.isLoanOpen)
  const setLoanOpen = useStore((state) => state.setLoanOpen)

  const handleLoanOpen = () => setLoanOpen(true)

  const handleLoanClose = () => setLoanOpen(false)

  return (
    <>
      {showAddBtn ? (<Button
        className={cn(styles.edit_controller)}
        variant="secondary"
        onClick={handleLoanOpen}
      >
        add
      </Button>
      ) : (
        <IconButton className={styles.edit} onClick={handleLoanOpen}>
          <DotsIcon />
        </IconButton>
      )}
      <Modal
        narrow
        title={`Loan ‘${request.project.title}’`}
        width={740}
        open={isLoanOpen}
        onCancel={handleLoanClose}
      >
        <LoanForm request={request} onClose={handleLoanClose} />
      </Modal>
    </>
  )
}
